import React, { useRef, useState, useEffect } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { Auth, cqAPI, getBid, loadPatients, getUid, LocationSelectorManager, getParam } from "../../lib/Auth";
import AsyncSelect from "react-select/async";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import * as Icons from "@fortawesome/free-solid-svg-icons";
import * as BrandIcons from '@fortawesome/free-brands-svg-icons';
import moment from "moment";
import {
  Menu,
  MenuButton,
  MenuList,
  Button,
  Flex,
  MenuItem as ChakraMenuItem,
  useColorMode,
  useTheme,
  Divider,
  useColorModeValue
} from "@chakra-ui/react";
import {
  faBars,
  faChevronDown,
  faChevronUp,
  faSearch,
  faChevronRight,
} from "@fortawesome/free-solid-svg-icons";
import debounce from "lodash.debounce";
import { components } from "react-select";
import "./MenuComponent.css";
import { useAppContext } from "../../AppContext";
import LogoComponent from "../Logo/LogoComponent";
const _ = require("lodash");

// Ensure LocationSelectorManager is defined or imported

const pathValidationExceptions = [
  "/history",
  "/rateappt",
  "/password_change",
  "/verify",
  /\/payroll\/me\//,
  "/sign",
  "/appointments/new",
  /\/appointments\/new\/service\/.*/,
  /\/monitoring\/calls\/.*/,
  /customize-ai.*/,
  /\/prompts\/po-prompt\/.*/,
  /\/prompts\/vam-prompt\/.*/,
  /\/prompts\/vav-prompt\/.*/
];

export const searchParamsToRemove = [
  'provider_id',
  'app_id',
  'timepref'
];

export default function MenuComponent({ toggleMenuCollapse, isCollapsed, isMobile }) {
  const {
    chatContext,
    setChatContext,
    selectedBusiness,
    setSelectedBusiness,
    locations,
    setLocations,
    realtime,
    realtimeInitialized,
    isPatient,
    setMenuLoaded,
    setIntercomVisible,
    intercomVisible
  } = useAppContext();

  const menuListStyle = {
    maxHeight: "80vh",
    overflowY: "auto",
  };
  const [menuItems, setMenuItems] = useState([]);
  const [patientInfo, setPatientInfo] = useState({});
  const itemBadge = useRef({});
  const badges = useRef({});
  const [badgeValue, setBadgeValue] = useState({});
  const [expandedState, setExpandedState] = useState({});
  const [errorMessage, setErrorMessage] = useState(null); 

  const navigate = useNavigate();
  const location = useLocation();
  const { colorMode } = useColorMode();
  const theme = useTheme();
  const bgColor = colorMode === "light" ? theme.colors.lightBackground : "#212330";
  const searchBgColor = colorMode === "light" ? "#f0f4f8" : "#2d3748";
  const textColor = colorMode === "light" ? theme.colors.lightText : theme.colors.darkText;
  const secondaryTextColor = colorMode === "light" ? theme.colors.gray[600] : theme.colors.gray[400];
  const hoverBgColor = useColorModeValue(theme.colors.gray[200], theme.colors.gray[700])
  const isPartner = window.location.hostname.includes('adec') || window.location.hostname.includes('a-dec');

  const manageChatContext = () => {
    const selectedPath = location.pathname.split("/").filter((x) => !!x);
    if (!selectedPath[0]) {
      setChatContext({ type: "oracle", name: "Oracle" });
      return;
    }
    switch (selectedPath[0]) {
      case "po":
        setChatContext({
          type: "practice_optimizer",
          name: "Practice Optimizer",
        });
        break;
      case "patient":
        setChatContext({ type: "patient", for: selectedPath[1] });
        break;
      case "oracle":
        setChatContext({ type: "oracle", name: "Oracle" });
        break;
      case "history":
        break;
      default:
        if (chatContext && chatContext.type) {
          setChatContext({});
        }
    }
  };

  const manageFooterExpansionState = () => {
    const newValue = location.pathname
      .split("/")
      .filter((x) => !!x)
      .slice(0, -1)
      .reduce((acc, key) => {
        acc[key] = true;
        return acc;
      }, {});
    if (!_.isEqual(newValue, expandedState)) {
      setExpandedState(newValue);
    }
  };

  useEffect(() => {
    manageChatContext();
    manageFooterExpansionState();
  }, [location.pathname]);

  useEffect(() => {
    const bid = getBid();
    const updateIt = async () => {
      const locs = await fetchLocations();
      const pageBid = getParam('bid')
      if (locs?.length === 1 && !pageBid && !Auth?.user?.isSuperAdmin) {
        const searchParams = new URLSearchParams(location.search);
        searchParams.set("bid", locs[0].bid);
        navigate(`${location.pathname}?${searchParams.toString()}`);
        return;
      }
      if (locs?.length && bid && selectedBusiness.bid !== bid) {
        const s = locs.find((x) => x.bid === bid);
        if (s) {
          if (!_.isEqual(selectedBusiness, s)) {
            setSelectedBusiness(s);
          }
          localStorage.setItem("bid", s.bid);
        }
      } else {
        if (selectedBusiness.bid) {
          setSelectedBusiness({});
        }
        localStorage.removeItem("bid");
      }
    };
    updateIt();
  }, [location.search]);

  const updateBadge = (res, title) => {
    const v = res?.data && Object.keys(res.data).reduce((acc, cur) => (acc += res.data[cur]), 0);
    if (v !== itemBadge.current[title]) {
      itemBadge.current = {
        ...itemBadge.current,
        [title]: v,
      };
      setBadgeValue(itemBadge.current);
    }
  };

  useEffect(() => {
    const sumOfBadgeValues = Object.values(badgeValue).reduce((acc, value) => acc + value, 0);
    if (sumOfBadgeValues === 0) {
      document.title = `${document.title.split(" (")[0]}`;
      return;
    }
    document.title = `${document.title.split(" (")[0]} (${sumOfBadgeValues})`;
  }, [badgeValue]);

  function MenuItem({
                      title,
                      icon,
                      params,
                      children,
                      onClick,
                      selectable = true,
                      isOnline,
                      path,
                      badge,
                      parentPath = [],
                    }) {
    const currentPath = _.flatten([...parentPath, ...(path ? [path] : [])].map(p=>p.split('/')))

    const enhancedChildren = React.Children.map(children, (child) => {
      if (React.isValidElement(child)) {
        return React.cloneElement(child, { parentPath: currentPath });
      }
      return child;
    });

    const selectedPath = location.pathname.replace(/\/[a-f0-9]{24}$/, '').split("/").filter((x) => !!x);
    const normalizedCurrentPath = currentPath.map((seg) => seg.replace(/^[a-f0-9]{24}$/, '')).filter(Boolean);

    const isSelected =
      selectable &&
      ((!!normalizedCurrentPath.length &&
        normalizedCurrentPath.length === selectedPath.length) || (!normalizedCurrentPath.length && !selectedPath.length)) &&
      normalizedCurrentPath.reduce((acc, cur, index) => {
        if (!acc) {
          return false;
        }
        return cur.replace(/\/[a-f0-9]{24}/, "") === selectedPath[index];
      }, true);

    const itemClass = `menu-item ${isSelected ? "selected" : ""}`;

    const onlineIndicatorStyle = {
      width: "10px",
      height: "10px",
      borderRadius: "50%",
      backgroundColor: isOnline ? "green" : "gray",
      marginRight: "5px",
    };

    const handleMenuItemClick = (title, params) => {
      if (!children) {
        const newSearchParams = new URLSearchParams(window.location.search);
        for (const key in params || {}) {
          if (params.hasOwnProperty(key)) {
            newSearchParams.set(key, params[key]);
          }
        }
        searchParamsToRemove.map(key => newSearchParams.delete(key));
        navigate(`/${currentPath.join("/")}?${newSearchParams.toString()}`);
        if (isMobile) {
          toggleMenuCollapse();
        }
      }
    };

    const onClickWrapper = (event) => {
      if (children) {
        setExpandedState({
          ...expandedState,
          [path]: path in expandedState ? !expandedState[path] : true,
        });

        // If the menu is collapsed and a chevron is clicked, expand the menu
        if (isCollapsed) {
          toggleMenuCollapse();
        }
      }

      event && event.stopPropagation();

      if (onClick) {
        onClick.call(this);
      }

      if (selectable) {
        handleMenuItemClick(title, params);
      }
    };

    useEffect(() => {
      if (!realtime.current) {
        return;
      }
      if (badge) {
        if (!badges.current[title]) {
          cqAPI.get(badge.endpoint).then((x) => updateBadge(x, title));
          badges.current[title] = {
            ep: badge.endpoint,
            updaters: realtime.current.subscribeToTopic(badge.topic, getBid(), function (data) {
              cqAPI.get(badge.endpoint).then((x) => updateBadge(x, title));
            }),
          };
        }
      }
    }, [realtimeInitialized, realtime, badge, badges]);

    return (
      <>
        <div className={itemClass} onClick={onClickWrapper}>
          {icon && <FontAwesomeIcon color={theme.colors.iconColor} icon={icon} className="fa-icon fa-fw"/>}
          {isOnline !== undefined && <div style={onlineIndicatorStyle}></div>}
          <span>{title}</span>
          {children && (
            <FontAwesomeIcon
              icon={expandedState[path] ? faChevronUp : faChevronDown}
              className="menu-chevron"
            />
          )}
          {((title in badgeValue) && (badgeValue[title] !== 0)) === true && (
            <div className="badge">{badgeValue[title]}</div>
          )}
        </div>
        {expandedState[path] && <div className="menu-subitems">{enhancedChildren}</div>}
      </>
    );
  }

  const CustomOption = (props) => {
    return (
      <components.Option {...props}>
        <div className="custom-option">
          <div className="custom-option-details">
            <div className="custom-option-name">{props.data.label}</div>
            <div className="custom-option-email">{props.data.email}</div>
            {props.data.dob && <div className="custom-option-email">DoB: {moment(props.data.dob).calendar()}</div>}
          </div>
          <FontAwesomeIcon icon={faChevronRight} className="custom-option-arrow"/>
        </div>
      </components.Option>
    );
  };

  const DropdownIndicator = (props) => {
    return (
      components.DropdownIndicator && (
        <components.DropdownIndicator {...props}>
          <FontAwesomeIcon icon={faSearch}/>
        </components.DropdownIndicator>
      )
    );
  };

  const customStyles = ({
                          width = 201,
                          height,
                          paddingTop = 0,
                          paddingBottom = 0,
                          paddingLeft = 8,
                          paddingRight = 8,
                        }) => ({
    container: (provided) => ({
      ...provided,
      width: `${width}px`,
    }),
    option: (provided, state) => ({
      ...provided,
      backgroundColor: state.isFocused
        ? hoverBgColor // Dynamic color when option is focused
        : bgColor,     // Dynamic default option background color
    }),
    control: (provided) => ({
      ...provided,
      minHeight: `${height}px`,
      height: `${height}px`,
      justifyContent: "flex-start",
      backgroundColor: searchBgColor,
      padding: 0,
      boxShadow: "none",
      borderRadius: "20px",
      border: "none",
    }),
    menu: (provided) => ({
      ...provided,
      width: "231px",
      marginTop: "0",
      marginBottom: "0",
      position: "absolute",
      left: "-10px",
      backgroundColor: bgColor
    }),
    valueContainer: (provided) => ({
      ...provided,
      height: `${height}`,
      padding: `${paddingTop}px ${paddingRight}px ${paddingBottom}px ${paddingLeft}px`,
    }),
    placeholder: (provided) => ({
      ...provided,
      textAlign: "left",
      color: secondaryTextColor, // Dynamic placeholder text color
    }),
    input: (provided) => ({
      ...provided,
      margin: 0,
      padding: 0,
      color: textColor, // Dynamic input text color
      height: `${height - paddingTop - paddingBottom}px`,
    }),
    indicatorsContainer: (provided) => ({
      ...provided,
      height: `${height}`,
    }),
    clearIndicator: (provided) => ({
      ...provided,
      padding: "5px",
    }),
  });

  const fetchLocations = async () => {
    const bid = getBid();
    const nobid = !bid;
    const loadedLocations = await cqAPI.get("/locations" + (nobid ? "?bid=" : ""));

    if (loadedLocations.type === 'error') {
      setErrorMessage(loadedLocations.msg);
      setMenuLoaded(true);
      return [];                                 
    }

    setErrorMessage(null);
    setLocations(loadedLocations.data);
    return loadedLocations.data;
  };

  const handleSelectChange = (selectedOption) => {
    const bid = getBid();
    window.open(`/?bid=${bid}&_uid=${selectedOption._id}`, '_blank');
  };

  const _loadSuggestions = (query, callback) => {
    loadPatients(query, false).then(callback);
  };

  const debouncedLoadPatients = debounce(_loadSuggestions, 1000);

  function isValidPath(data, path, prefix = "") {
    if (pathValidationExceptions.find((allowed) => (allowed.test ? allowed.test(path) : allowed === path))) {
      return true;
    }

    path = path === "" ? "/" : path;

    for (const item of data.filter(p => 'path' in p)) {
      let currentPath = `${prefix}/${(item.path || "").replace(/\/[a-f0-9]{24}$/, "")}`.replace(/\/+$/, "");
      currentPath = currentPath === "" ? "/" : currentPath;
      if (currentPath === path || currentPath + "/" === path) {
        return true;
      }

      if (item.children) {
        if (isValidPath(item.children, path, currentPath)) {
          return true;
        }
      }
    }

    return false;
  }

  useEffect(() => {
    const fetchMenuItems = async () => {
      const bid = getBid();
      const uid = getUid();
      if (uid) {
        const response = await cqAPI.get("/isloggedin");
        setPatientInfo(response.data);
      }
      if (!Auth?.user?.isSuperAdmin) {
        if (!uid && (!selectedBusiness.bid && !bid)) {
          return;
        }
      }
      try {
        const response = await cqAPI.get(`/menu?bid=${bid}`);
        if (response.type === "ok") {
          setMenuLoaded(true)
          setMenuItems(response.data);
          if (!isValidPath(response.data, location.pathname.replace(/\/[a-f0-9]{24,40}$/, ""))) {
            const responsePath = response.data?.length && response.data[0].path;
            const newSearchParams = new URLSearchParams(window.location.search);
            navigate(`/${responsePath}?${newSearchParams.toString()}`);
          }
        }
      } catch (error) {
        console.error("Error fetching patients:", error);
      }
    };
    fetchMenuItems();
  }, [location.search, selectedBusiness]);

  const actions = {
    logOut: () => {
      Auth.signOut().then(() => {
        navigate("/");
      });
    },
    showIntercom: ()=>{
      setIntercomVisible(!intercomVisible)
    },
    switchPatient: (params)=>{
      navigate(`/?_uid=${params._uid}&bid=${getParam('bid')}`)
    }
  };

  const generateMenuItems = (items) => {
    return items.map((item) => (
      <MenuItem
        key={item.title}
        title={item.title}
        icon={item.icon ? Icons[item.icon] || BrandIcons[item.icon] : undefined}
        path={item.path}
        params={item.params}
        selectable={item.selectable !== false}
        onClick={item.onClick && actions[item.onClick]? ()=>actions[item.onClick](item.params) : null}
        badge={item.badge}
      >
        {item.children && generateMenuItems(item.children)}
      </MenuItem>
    ));
  };

  if (new URLSearchParams(window.location.search).get("fs") === "1") {
    return <div style={{ width: "0px" }}></div>;
  }

  // FULL-PAGE OVERLAY WHEN LOCATIONS ERROR
  if (errorMessage) { // <-- NEW
    return (
      <div
        style={{
          position: "fixed",
          inset: 0,
          zIndex: 9999,
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          backgroundColor: "rgba(0,0,0,0.4)",
        }}
      >
        <div
          style={{
            backgroundColor: "#ffffff",
            padding: "24px 32px",
            borderRadius: "12px",
            maxWidth: "480px",
            width: "90%",
            textAlign: "center",
            fontSize: "1rem",
          }}
        >
          {errorMessage}
        </div>
      </div>
    );
  }

  if (!menuItems.length) {
    return <div></div>;
  }

  return (
    <div
      className={`menu-container left-menu ${
        !isMobile && isCollapsed ? "collapsed" : "uncollapsed"
      } ${isMobile && !isCollapsed ? "mobile-open" : ""} ${
        isMobile && isCollapsed ? "mobile-menu-hidden" : ""
      }`}
    >
      <div className="menu-header">

        {!isCollapsed && <div className="logo-burger-container">
          <div className="logo">
            <LogoComponent isCollapsed={isCollapsed} width={"93px"} ml={"100px"} mx="auto" mb={4} ></LogoComponent>
          </div>

          <FontAwesomeIcon
            icon={faBars}
            className="menu-burger-icon"
            onClick={toggleMenuCollapse}
          />
        </div>}

        {isCollapsed && <div className="menu-burger-icon">
          <FontAwesomeIcon
            icon={faBars}
            onClick={toggleMenuCollapse}
          />
        </div>}

        {!isCollapsed && isPatient && <h3 id={"patient-name"}>{patientInfo?.user?.name}</h3>}

        {!isPatient && !Auth?.user?.isPartnerAdmin && (!isPartner || !window.location.pathname.includes('inbox')) &&
          !isCollapsed && getBid() && <AsyncSelect
            id={"search-patient-menu"}
            loadOptions={debouncedLoadPatients}
            placeholder="Search patients"
            components={{ DropdownIndicator, Option: CustomOption }}
            className="search-input"
            styles={customStyles({ width: "100%", height: 34 })}
            onChange={handleSelectChange}
            value={""} // for value to reset after selecting a result
          />
        }

        {!isPatient && !Auth?.user?.isPartnerAdmin &&
          (!isPartner || !window.location.pathname.includes('inbox')) &&
          isCollapsed &&
          !isMobile &&
          <FontAwesomeIcon
            icon={faSearch}
            className="search-icon"
            onClick={toggleMenuCollapse}
          />
        }

        <Divider borderColor="gray.200" my="0.5rem"/>

      </div>
      {!(isMobile && isCollapsed) && (<div className="menu-content">{generateMenuItems(menuItems)}</div>)}
      {
        !isCollapsed && Auth.user && (
          <div className="menu-footer">
            <Menu placement="top">
              {({ isOpen }) => (
                <>
                  <div className="loggin-info">logged in as {patientInfo?.user?.name}</div>
                  {getBid() && <>
                    <MenuButton as={Button} variant="ghost" size="sm" width="full">
                      <Flex justifyContent="space-between" alignItems="center" width="full">
                        <span className="practice-button-text" id={"current-group"} >{selectedBusiness.name}</span>
                        <FontAwesomeIcon icon={isOpen ? faChevronUp : faChevronDown}/>
                      </Flex>
                    </MenuButton>
                    <MenuList sx={menuListStyle} zIndex="popover">
                      {(locations || []).map((loc) => (
                        <ChakraMenuItem
                          key={loc.bid}
                          onClick={() => {
                            const newSearchParams = new URLSearchParams(window.location.search);
                            newSearchParams.set("bid", loc.bid);
                            navigate(`${window.location.pathname}?${newSearchParams.toString()}`);
                          }}
                        >
                          {loc.groupname || loc.name}
                        </ChakraMenuItem>
                      ))}
                      {selectedBusiness && selectedBusiness.bid && (
                        <ChakraMenuItem
                          key="none"
                          onClick={async () => {
                            setSelectedBusiness({});
                            localStorage.removeItem("bid");
                            LocationSelectorManager.selectedLocation = null;
                            LocationSelectorManager.selectionPromise = null;
                            navigate('/locations');
                          }}
                        >
                          Select a different group
                        </ChakraMenuItem>
                      )}
                    </MenuList>
                  </>}
                </>
              )}
            </Menu>
          </div>
        )
      }
    </div>
  )
    ;
}
